import { useHistory } from 'react-router-dom';

import { Button } from 'modules/v2/common/AtomicDesign/atoms';
import {
  getRouteBuyCredits,
  getRouteMailOnDemandOrder,
} from 'modules/v2/routes/navigation';
import { usePrintCreditsBalance } from 'modules/v2/common/hooks/usePrintCreditsBalance';

import CreditsBalanceWidget from '../../components/CreditsBalanceWidget';
import CoverLetterMiniPreview from '../../components/CoverLetterMiniPreview';
import strings, { formatMessage } from '../../strings';
import { formatCredits } from '../../utils';

const PayStep = ({
  order,
  onPay,
  onBack,
  paying,
  processing = false,
  payingTimedOut = false,
  payError,
}) => {
  const history = useHistory();
  const { data: balanceData } = usePrintCreditsBalance();

  const pricing = order?.pricing || {};
  const resolution = order?.resolution || {};

  // While the debit is in flight (a fresh click) or a resumed PAYING order is
  // still settling, the action buttons are locked. Once the poll gives up
  // (payingTimedOut) the buttons unlock so the member can retry or leave.
  const locked = paying || processing;

  const balance = balanceData?.balance;
  const { creditsRequired } = pricing;
  const balanceAfter =
    typeof balance === 'number' && typeof creditsRequired === 'number'
      ? balance - creditsRequired
      : null;

  // Proactive shortfall: when the balance can't cover the mailing, surface it
  // BEFORE the member commits (and gate the Pay button) instead of dead-ending
  // on a 402. We only know the shortfall once the balance has loaded.
  const isShort = balanceAfter !== null && balanceAfter < 0;
  const shortfall = isShort ? creditsRequired - balance : 0;
  // After a 402 the reactive insufficient block already covers it — don't show
  // two near-identical banners.
  const showShortfall = isShort && payError?.type !== 'insufficient';

  // Reactive 402 fields, defended against a missing field so the copy can never
  // render "undefined" (DS now sends these — #1991 — this is belt-and-suspenders):
  // the `?? 0` guards the case where the balance query is transiently empty when
  // a 402 lands, and formatCredits (below) also coerces undefined → "0".
  const insufficientRequired = payError?.required ?? creditsRequired ?? 0;
  const insufficientBalance = payError?.balance ?? balance ?? 0;
  const insufficientShortfall =
    payError?.shortfall ??
    Math.max(0, insufficientRequired - insufficientBalance);

  return (
    <div>
      <h2 className="mb-6 text-lg font-semibold">{strings.pay.title}</h2>

      {processing && (
        <div
          className="mb-6 max-w-xl rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800"
          data-testid="mod-pay-processing"
        >
          {strings.pay.processingResume}
        </div>
      )}

      {payingTimedOut && (
        <div
          className="mb-6 max-w-xl rounded-lg border border-amber-300 bg-amber-50 p-4"
          data-testid="mod-pay-stuck"
        >
          <h4 className="mb-1 font-semibold">{strings.pay.payingStuckTitle}</h4>
          <p className="mb-3 text-sm text-neutral-600">{strings.pay.payingStuckBody}</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={onPay} data-testid="mod-pay-retry-stuck">
              {strings.pay.payingRetry}
            </Button>
            <Button
              color="outlined"
              size="sm"
              onClick={() => history.push(getRouteMailOnDemandOrder(order?._id))}
              data-testid="mod-pay-view-order"
            >
              {strings.pay.payingViewOrder}
            </Button>
          </div>
        </div>
      )}

      <div className="mb-6 max-w-xl rounded-lg border border-neutral-200 bg-white p-4">
        {(order?.asset?.thumbnailUrl || order?.coverLetter) && (
          <div className="mb-4 flex items-start gap-3" data-testid="mod-pay-thumbnails">
            {order?.asset?.thumbnailUrl && (
              <img
                src={order.asset.thumbnailUrl}
                alt={formatMessage(strings.pay.confirmationAssetAlt, {
                  title: order?.asset?.title || '',
                })}
                className="h-20 w-16 shrink-0 rounded border border-neutral-200 object-cover"
                loading="lazy"
                data-testid="mod-pay-book-thumbnail"
              />
            )}
            {order?.coverLetter && (
              <div className="shrink-0 overflow-hidden rounded border border-neutral-200">
                <CoverLetterMiniPreview
                  templateKey={order.coverLetter.templateKey}
                  body={order.coverLetter.editedBody}
                  width={62}
                />
              </div>
            )}
          </div>
        )}
        <table className="w-full text-sm">
          <tbody>
            <tr className="border-b border-neutral-100">
              <td className="py-2 text-neutral-500">{strings.pay.summaryAsset}</td>
              <td className="py-2 text-right font-medium">{order?.asset?.title}</td>
            </tr>
            <tr className="border-b border-neutral-100">
              <td className="py-2 text-neutral-500">{strings.pay.summaryRecipients}</td>
              <td className="py-2 text-right font-medium">
                {formatCredits(
                  resolution.selectedMailableCount ?? resolution.mailableCount,
                )}
              </td>
            </tr>
            <tr>
              <td className="py-2 text-neutral-500">{strings.pay.summaryCost}</td>
              <td className="py-2 text-right font-semibold">
                {formatCredits(pricing.creditsRequired)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mb-6 max-w-xl">
        <CreditsBalanceWidget />
        {balanceAfter !== null && balanceAfter >= 0 && (
          <p
            className="mt-2 text-sm text-neutral-500"
            data-testid="mod-balance-after"
          >
            {strings.pay.balanceAfterLabel}:{' '}
            <span className="font-semibold text-neutral-800">
              {formatCredits(balanceAfter)}
            </span>
          </p>
        )}
      </div>

      {showShortfall && (
        <div
          className="mb-6 flex max-w-xl items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4"
          data-testid="mod-shortfall-banner"
        >
          <div className="flex-1">
            <h4 className="mb-1 font-semibold text-amber-900">
              {formatMessage(strings.pay.shortfallTitle, {
                shortfall: formatCredits(shortfall),
              })}
            </h4>
            <p className="text-sm text-amber-800">
              {formatMessage(strings.pay.shortfallBody, {
                required: formatCredits(creditsRequired),
                balance: formatCredits(balance),
              })}
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => history.push(getRouteBuyCredits())}
            data-testid="mod-shortfall-buy-credits"
          >
            {strings.pay.buyCredits}
          </Button>
        </div>
      )}

      {payError?.type === 'insufficient' && (
        <div
          className="mb-6 max-w-xl rounded-lg border border-error-500 bg-error-50 p-4"
          data-testid="mod-insufficient-credits"
        >
          <h4 className="mb-1 font-semibold">{strings.pay.insufficientTitle}</h4>
          <p className="mb-3 text-sm text-neutral-600">
            {formatMessage(strings.pay.insufficientBody, {
              required: formatCredits(insufficientRequired),
              balance: formatCredits(insufficientBalance),
              shortfall: formatCredits(insufficientShortfall),
            })}
          </p>
          <div className="flex gap-2">
            <Button
              color="outlined"
              size="sm"
              onClick={() => history.push(getRouteBuyCredits())}
              data-testid="mod-buy-credits"
            >
              {strings.pay.buyCredits}
            </Button>
            <Button size="sm" onClick={onPay} disabled={paying} data-testid="mod-retry-pay">
              {strings.pay.retryPay}
            </Button>
          </div>
        </div>
      )}

      {payError?.type === 'generic' && (
        <div className="mb-6 max-w-xl rounded-lg border border-error-500 bg-error-50 p-4 text-sm">
          {strings.pay.payError}
        </div>
      )}

      <p className="mb-6 text-sm text-neutral-500">{strings.pay.noChargeNote}</p>

      <div className="flex items-center justify-between">
        <Button color="outlined" onClick={onBack} disabled={locked}>
          {strings.common.back}
        </Button>
        <div className="flex flex-col items-end gap-1">
          <Button
            onClick={onPay}
            disabled={locked || isShort}
            data-testid="mod-pay-button"
            aria-describedby={
              isShort && !locked ? 'mod-pay-disabled-hint' : undefined
            }
          >
            {locked
              ? strings.pay.paying
              : formatMessage(strings.pay.payButton, {
                creditsRequired: formatCredits(creditsRequired),
              })}
          </Button>
          {isShort && !locked && (
            <span
              id="mod-pay-disabled-hint"
              className="text-xs text-amber-700"
              data-testid="mod-pay-disabled-hint"
            >
              {formatMessage(strings.pay.payDisabledHint, {
                shortfall: formatCredits(shortfall),
              })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayStep;
